/** @jsx jsx */
// =============================================================
// job2.js – Neue Job-Detailseite (Staging, Ticket 20022)
//
// Pixelgenau nach Adobe-XD "Web 1920 – 32" (Florian / KR8):
//   "JOBS"-Übertitel 120px, Datumsspalte 420px mit 120px-Datum,
//   Titel 120px/110, MWD-Kreise 82px (#00FF27, 2px), Einleitung
//   50px/70, Balken mit Verlauf + 50px-Label, Listen 18px/25 mit
//   Smiley-Bullets, "Die Details" 50px/70 mit Doppelstern,
//   Kultur-Icons 124px in 2 Spalten, Bewerbungsformular mit
//   546×70-Feldern, PDF-Upload, Radio-Kreisen und Drehbadge.
//   Skalierung proportional zur Containerbreite (cqw, Basis 1920).
//
// Inhaltsverzeichnis:
//   1. Imports & Setup
//   2. Skalierungs-Helfer & Keyframes
//   3. Styled Components
//   4. Hauptkomponente Caly_Job2Template
//   5. GraphQL-Abfrage & Export
// =============================================================

// ─────────────────────────────────────────────────────────────
// 1. Imports & Setup
// ─────────────────────────────────────────────────────────────
import React from 'react'
import { jsx, keyframes } from '@emotion/core'
import styled from '@emotion/styled'
import { graphql } from 'gatsby'
import Layout from '../components/Layout'
import Container from '../components/Container'
import SEO from '../components/SEO'
import Newsletter from '../components/NewsletterPopup'
import Menu from '../components/Menu'
import MenuBottom from '../components/MenuBottom'
import caly_kulturDaten from '../components/caly_KulturDaten'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'

// ─────────────────────────────────────────────────────────────
// 2. Skalierungs-Helfer & Keyframes
// ─────────────────────────────────────────────────────────────

// Design-Pixel (1920er-Artboard) -> Container-Prozent mit Untergrenze
function caly_px(designPx, min) {
  const caly_cqw = (designPx / 19.2).toFixed(3)
  return `clamp(${min}px, ${caly_cqw}cqw, ${designPx}px)`
}

// Langsame Rotation für den "Danke für deine Bewerbung"-Badge
const caly_rotateAnimation = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

// ─────────────────────────────────────────────────────────────
// 3. Styled Components
// ─────────────────────────────────────────────────────────────

// Seiten-Wrapper: definiert den Container für die cqw-Skalierung
const Caly_Seite = styled.div`
  width: 100%;
  container-type: inline-size;
  position: relative;

  /* Gleicher Seitenrahmen wie der Header (ab "large"), damit die
     Menüspalte exakt mit der Social-Spalte des Headers fluchtet */
  @media (min-width: ${props => props.theme.responsive.large}) {
    border-left: 2px solid #000;
    border-right: 2px solid #000;
  }
`

// Hauptmenü als rechte Spalte im Kopfbereich (wie im XD-Design):
// nutzt die bestehenden Menu/MenuBottom-Komponenten der Seite.
// Die Spalte liegt IM Fluss (kein Overlay) – ihre natürliche Höhe
// bestimmt die Höhe der Kopfzeile, darunter ist kein Leerraum.
const Caly_MenuSpalte = styled.div`
  flex: 0 0 280px;
  width: 280px;
  display: flex;
  flex-direction: column;

  nav li {
    background: #fff;
  }

  nav li.dark {
    background: #000;
  }

  @media (max-width: ${props => props.theme.responsive.mediumMax}) {
    display: none;
  }
`

// Kopfzeile: "JOBS"-Titel links, Menüspalte rechts. Die Zeile ist
// genau so hoch wie das Menü (kein Leerraum darunter), die Linie
// läuft unter beiden durch. Titel sitzt wie im XD unten an der Linie.
const Caly_KopfZeile = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  border-bottom: 2px solid #000;
`

// "JOBS"-Übertitel (Design: 120px, unten an der Linie ausgerichtet).
// Die Zelle streckt sich auf volle Kopfzeilenhöhe und trägt die
// Trennlinie zum Menü (wie im Header, fluchtet mit Social-Spalte).
const Caly_JobsUebertitel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-end;
  font-size: ${caly_px(120, 40)};
  line-height: 0.917;
  text-transform: uppercase;
  padding: ${caly_px(60, 24)} 0 ${caly_px(30, 12)} ${caly_px(55, 15)};

  @media (min-width: ${props => props.theme.responsive.medium}) {
    border-right: 2px solid #000;
  }
`

// Kopfbereich: Datum links (420px, Linie rechts), Inhalt rechts
const Caly_Kopf = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: ${props => props.theme.responsive.medium}) {
    flex-direction: row;
  }
`

// Linke Datumspalte (Design: 420px breit, 120px-Datum bei x55/y741)
const Caly_DatumSpalte = styled.div`
  flex: 0 0 auto;
  font-size: ${caly_px(120, 34)};
  line-height: 0.917;
  padding: ${caly_px(60, 20)} ${caly_px(15, 8)} 0 ${caly_px(55, 15)};

  @media (min-width: ${props => props.theme.responsive.medium}) {
    width: ${caly_px(420, 90)};
    border-right: 2px solid #000;
    min-height: ${caly_px(880, 200)};
  }
`

// Rechte Kopfspalte (Inhalt ab Design-x457)
const Caly_KopfRechts = styled.div`
  flex: 1;
  min-width: 0;
  padding: ${caly_px(60, 20)} ${caly_px(45, 15)} ${caly_px(50, 20)}
    ${caly_px(37, 15)};
`

// Jobtitel (Design: 120px/110)
const Caly_Titel = styled.h1`
  font-size: ${caly_px(120, 36)};
  line-height: 0.917;
  text-transform: uppercase;
  margin: 0 0 ${caly_px(60, 20)} 0;
  word-break: break-word;

  @media (min-width: ${props => props.theme.responsive.medium}) {
    word-break: normal;
  }
`

// Reihe der M/W/D-Kreise
const Caly_SexReihe = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${caly_px(23, 8)};
  margin-bottom: ${caly_px(100, 30)};
`

// Einzelner Kreis (Design: 82×80, Rahmen 2px #00FF27, Buchstabe 50px)
const Caly_Sex = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${caly_px(50, 16)};
  width: ${caly_px(82, 30)};
  height: ${caly_px(80, 30)};
  border-radius: 999px;
  border: 2px solid ${props => props.theme.colors.green};
`

// Einleitungstext (Design: 50px/70, Breite 1412)
const Caly_Einleitung = styled.div`
  font-size: ${caly_px(50, 17)};
  line-height: 1.4;
  max-width: ${caly_px(1412, 700)};
`

// Inhaltsbereich unterhalb des Kopfes: linke Leerspalte + Inhalt
const Caly_InhaltRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`

// Leere linke Spalte (führt die Datumslinie fort, nur Desktop)
const Caly_InhaltLinks = styled.div`
  display: none;

  @media (min-width: ${props => props.theme.responsive.medium}) {
    display: block;
    flex: 0 0 auto;
    width: ${caly_px(420, 90)};
    border-right: 2px solid #000;
  }
`

// Rechter Inhaltsbereich
const Caly_InhaltRechts = styled.div`
  flex: 1;
  min-width: 0;
`

// Farbiger Abschnitts-Balken (Design: ~90px hoch, Label 50px)
const Caly_Balken = styled.div`
  display: flex;
  align-items: center;
  font-size: ${caly_px(50, 18)};
  min-height: ${caly_px(90, 44)};
  border-top: 2px solid #000;
  border-bottom: 2px solid #000;
  padding: ${caly_px(8, 4)} ${caly_px(37, 15)};

  &.caly-gruen {
    background: transparent
      linear-gradient(90deg, #00ff27 0%, #9effac 60%, #ffffff 100%) 0% 0%
      no-repeat padding-box;
  }

  &.caly-orange {
    background: transparent
      linear-gradient(90deg, #ed6941 0%, #fcfffc 96%, #ffffff 100%) 0% 0%
      no-repeat padding-box;
  }
`

// Inhalt unter einem Balken – Smiley-Bullets (Design: 18px/25)
const Caly_BalkenInhalt = styled.div`
  padding: ${caly_px(50, 20)} ${caly_px(45, 15)} ${caly_px(60, 24)}
    ${caly_px(37, 15)};
  font-size: ${caly_px(18, 13)};
  line-height: 1.389;

  ul {
    list-style: none;
    margin: 0;
  }

  ul li {
    padding-left: 1.7em;
    position: relative;
    margin-bottom: 0.45em;
  }

  ul li:before {
    content: '';
    background-image: url('/images/extendit-smile.svg');
    background-size: contain;
    background-position: left center;
    background-repeat: no-repeat;
    position: absolute;
    left: 0;
    top: 0.12em;
    display: block;
    width: 1.1em;
    height: 1.2em;
  }

  /* Absätze mit Zeilenumbrüchen (Soft-Breaks aus Contentful):
     jede Zeile wird wie ein Listenpunkt mit Smiley dargestellt */
  .caly-zeile {
    display: block;
    padding-left: 1.7em;
    position: relative;
    margin-bottom: 0.45em;
  }

  .caly-zeile:before {
    content: '';
    background-image: url('/images/extendit-smile.svg');
    background-size: contain;
    background-position: left center;
    background-repeat: no-repeat;
    position: absolute;
    left: 0;
    top: 0.12em;
    display: block;
    width: 1.1em;
    height: 1.2em;
  }
`

// "DIE DETAILS" Überschrift (Design: 120px/110)
const Caly_DetailsTitel = styled.div`
  font-size: ${caly_px(120, 36)};
  line-height: 0.917;
  text-transform: uppercase;
  border-top: 2px solid #000;
  padding: ${caly_px(70, 26)} ${caly_px(37, 15)} ${caly_px(50, 20)};
`

// Zweispaltiges Raster der Detail-Punkte (Design: Spalten ~413px)
const Caly_DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${caly_px(24, 10)} ${caly_px(60, 20)};
  padding: 0 ${caly_px(45, 15)} ${caly_px(80, 30)} ${caly_px(37, 15)};

  @media (min-width: ${props => props.theme.responsive.medium}) {
    grid-template-columns: ${caly_px(560, 260)} 1fr;
    max-width: ${caly_px(1250, 620)};
  }
`

// Einzelner Detail-Punkt mit Doppelstern (Design: 50px/70)
const Caly_DetailPunkt = styled.div`
  display: flex;
  align-items: center;
  font-size: ${caly_px(50, 17)};
  line-height: 1.4;

  svg {
    width: ${caly_px(46, 18)};
    height: ${caly_px(47, 18)};
    flex: 0 0 auto;
    margin-right: ${caly_px(28, 10)};
    fill: #000;
  }
`

// "SEI WILLKOMMEN BEI EXTENDIT" Überschrift (Design: 120px/110)
const Caly_WillkommenTitel = styled.div`
  font-size: ${caly_px(120, 36)};
  line-height: 0.917;
  text-transform: uppercase;
  border-top: 2px solid #000;
  padding: ${caly_px(70, 26)} ${caly_px(37, 15)} ${caly_px(60, 22)};
`

// Raster der 6 Kultur-Icons (Design: 2 Spalten, Icon 124px)
const Caly_KulturGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${caly_px(90, 30)} 0;
  padding: 0 ${caly_px(45, 15)} ${caly_px(100, 36)} ${caly_px(37, 15)};

  @media (min-width: ${props => props.theme.responsive.small}) {
    grid-template-columns: ${caly_px(421, 200)} 1fr;
    max-width: ${caly_px(1100, 500)};
  }
`

// Einzelne Kultur-Karte
const Caly_KulturKarte = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${caly_px(16, 8)};
`

// Icon-Box (Design: 124×124)
const Caly_KulturIcon = styled.div`
  height: ${caly_px(124, 56)};
  display: flex;
  align-items: flex-end;

  img {
    display: block;
    max-height: 100%;
    width: auto;
  }
`

// Kultur-Text (Design: 18px/25, Breite ~330)
const Caly_KulturText = styled.div`
  font-size: ${caly_px(18, 13)};
  line-height: 1.389;
  max-width: ${caly_px(330, 220)};
`

// Kontakt-Sektion
const Caly_KontaktSektion = styled.div`
  border-top: 2px solid #000;
  padding: ${caly_px(70, 26)} ${caly_px(45, 15)} ${caly_px(120, 40)}
    ${caly_px(37, 15)};
`

// "TRITT MIT UNS IN KONTAKT" Überschrift (Design: 120px/110)
const Caly_KontaktTitel = styled.div`
  font-size: ${caly_px(120, 36)};
  line-height: 0.917;
  text-transform: uppercase;
  margin-bottom: ${caly_px(50, 18)};
`

// Hinweistext (Design: 50px/70)
const Caly_KontaktHinweis = styled.div`
  font-size: ${caly_px(50, 16)};
  line-height: 1.4;
  margin-bottom: ${caly_px(80, 28)};
  max-width: ${caly_px(900, 500)};
`

// Formular-Zeile: Felder links, rotierender Badge rechts
const Caly_FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${caly_px(60, 24)};

  @media (min-width: ${props => props.theme.responsive.medium}) {
    flex-direction: row;
    align-items: flex-start;
    gap: ${caly_px(160, 40)};
  }
`

// Formular-Spalte (Design: Felder 546px breit)
const Caly_FormSpalte = styled.div`
  flex: 0 0 auto;
  width: 100%;

  @media (min-width: ${props => props.theme.responsive.medium}) {
    width: ${caly_px(546, 280)};
  }
`

// Eingabefeld (Design: 546×70, Rahmen 2px grau, Platzhalter grau)
const Caly_Input = styled.input`
  width: 100%;
  height: ${caly_px(70, 40)};
  border: 2px solid #b2b2b2;
  background: #fff;
  padding: 0 ${caly_px(18, 10)};
  font-size: ${caly_px(50, 16)};
  font-family: inherit;
  margin-bottom: ${caly_px(18, 10)};
  outline: none;

  &::placeholder {
    color: #b2b2b2;
  }

  &:focus {
    border-color: #000;
  }
`

// Kasten für die Datei-Uploads (Design: 2px schwarzer Rahmen)
const Caly_UploadBox = styled.div`
  border: 2px solid #000;
  padding: ${caly_px(24, 12)} ${caly_px(18, 10)};
  margin-bottom: ${caly_px(24, 12)};
  font-size: ${caly_px(18, 13)};
  line-height: 1.389;

  label {
    display: block;
    margin-bottom: ${caly_px(16, 8)};
    cursor: pointer;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  input[type='file'] {
    display: block;
    margin-top: 4px;
    font-family: inherit;
    max-width: 100%;
  }
`

// Kontaktoptions-Zeile (Design: Kreise wie M/W/D, Labels 50px)
const Caly_KontaktOptionen = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${caly_px(50, 18)};
  margin: ${caly_px(30, 12)} 0 ${caly_px(10, 6)};
  font-size: ${caly_px(50, 16)};

  label {
    display: flex;
    align-items: center;
    gap: ${caly_px(20, 8)};
    cursor: pointer;
  }

  input[type='radio'] {
    appearance: none;
    width: ${caly_px(82, 26)};
    height: ${caly_px(80, 26)};
    border: 2px solid ${props => props.theme.colors.green};
    border-radius: 50%;
    outline: none;
    cursor: pointer;
    flex-shrink: 0;

    &:checked {
      background: radial-gradient(
        circle,
        #000 0%,
        #000 55%,
        transparent 62%
      );
    }
  }
`

// Kleiner Hilfetext unter den Kontaktoptionen (Design: 18px)
const Caly_OptionenHinweis = styled.div`
  font-size: ${caly_px(18, 12)};
  line-height: 1.389;
  margin-bottom: ${caly_px(40, 16)};
`

// Buttons "File wählen"/"absenden" (Design: eckig, 2px orange)
const Caly_FormBtn = styled.button`
  display: block;
  border: 2px solid ${props => props.theme.colors.red};
  background: transparent;
  padding: ${caly_px(6, 3)} ${caly_px(40, 16)};
  font-size: ${caly_px(50, 16)};
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: ${props => props.theme.colors.red};
    color: #fff;
  }
`

// Rotierender "Danke für deine Bewerbung"-Badge (Design: ~300px)
const Caly_BadgeWrapper = styled.div`
  flex: 0 0 auto;
  width: ${caly_px(300, 150)};
  height: ${caly_px(300, 150)};
  position: relative;
  align-self: center;

  @media (min-width: ${props => props.theme.responsive.medium}) {
    align-self: flex-start;
    margin-top: ${caly_px(30, 12)};
  }

  svg.caly-badge-text {
    width: 100%;
    height: 100%;
    animation: ${caly_rotateAnimation} 18s linear infinite;
  }

  /* Grüner Doppelstern in der Mitte */
  svg.caly-badge-stern {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32%;
    height: 32%;
    fill: ${props => props.theme.colors.green};
  }
`

// ─────────────────────────────────────────────────────────────
// 4. Hauptkomponente Caly_Job2Template
// ─────────────────────────────────────────────────────────────

// Wiederverwendbarer Doppelstern (nach XD-Vorlage, 95×97)
function Caly_SternIcon({ className }) {
  return (
    <svg viewBox="0 0 95 97" className={className} aria-hidden="true">
      {/* großer Vierzack-Stern */}
      <path d="M57 32 C60 55 68 63 91 66 C68 69 60 77 57 97 C54 77 46 69 23 66 C46 63 54 55 57 32 Z" />
      {/* kleiner Vierzack-Stern oben links */}
      <path d="M24 0 C26 14 31 19 45 21 C31 23 26 28 24 42 C22 28 17 23 3 21 C17 19 22 14 24 0 Z" />
    </svg>
  )
}

// Rich-Text-Optionen: Zeilen mit Soft-Breaks (\n) werden als
// einzelne Smiley-Punkte gerendert (siehe .caly-zeile im CSS)
const caly_richTextOptionen = {
  renderText: function caly_renderText(text) {
    if (!text.includes('\n')) return text
    return text.split('\n').map(function caly_zeile(zeile, i) {
      return (
        <span className="caly-zeile" key={i}>
          {zeile}
        </span>
      )
    })
  },
}

const Caly_Job2Template = ({ data }) => {
  const {
    title,
    slug,
    metaDescription,
    publishDate,
    einleitung,
    section1,
    section2,
    sexm,
    sexw,
    sexd,
    arbeitszeit,
    gehalt,
    gehlterProJahr,
    eintritt,
    teilzeitMglich,
  } = data.contentfulJobs

  // Datum "02/05/24" in drei Zeilen aufteilen (wie im XD-Design)
  const caly_datumTeile = publishDate ? publishDate.split('/') : []

  // Detail-Punkte aus den Contentful-Feldern (Lesereihenfolge des XD)
  const caly_details = []
  if (arbeitszeit) caly_details.push(`${arbeitszeit} / Woche`)
  if (teilzeitMglich) caly_details.push('Teilzeit möglich')
  if (gehlterProJahr) caly_details.push(`${gehlterProJahr} Gehälter / Jahr`)
  if (gehalt) caly_details.push(gehalt)
  if (eintritt) caly_details.push(`Eintritt ${eintritt}`)

  return (
    <Layout>
      <Newsletter />
      <SEO
        title={title}
        description={
          metaDescription
            ? metaDescription.internal.content
            : einleitung.internal.content
        }
      />
      <Container>
        <Caly_Seite>
          {/* ── KOPFZEILE: "JOBS" links, Hauptmenü rechts ───────── */}
          <Caly_KopfZeile>
            <Caly_JobsUebertitel>Jobs</Caly_JobsUebertitel>
            <Caly_MenuSpalte>
              <Menu bottommenu={true} />
              <MenuBottom />
            </Caly_MenuSpalte>
          </Caly_KopfZeile>

          {/* ── KOPF: Datum | Titel + M/W/D + Einleitung ─────────── */}
          <Caly_Kopf>
            <Caly_DatumSpalte>
              {caly_datumTeile.map(function caly_renderDatum(caly_teil, caly_i) {
                return <div key={`datum-${caly_i}`}>{caly_teil}</div>
              })}
            </Caly_DatumSpalte>
            <Caly_KopfRechts>
              <Caly_Titel>{title}</Caly_Titel>
              <Caly_SexReihe>
                {sexm && <Caly_Sex>M</Caly_Sex>}
                {sexw && <Caly_Sex>W</Caly_Sex>}
                {sexd && <Caly_Sex>D</Caly_Sex>}
              </Caly_SexReihe>
              <Caly_Einleitung>{einleitung.internal.content}</Caly_Einleitung>
            </Caly_KopfRechts>
          </Caly_Kopf>

          <Caly_InhaltRow>
            <Caly_InhaltLinks />
            <Caly_InhaltRechts>
              {/* ── DEINE TÄTIGKEITEN ────────────────────────────── */}
              {section1 && (
                <React.Fragment>
                  <Caly_Balken className="caly-gruen">
                    Deine Tätigkeiten
                  </Caly_Balken>
                  <Caly_BalkenInhalt>
                    {documentToReactComponents(section1.json, caly_richTextOptionen)}
                  </Caly_BalkenInhalt>
                </React.Fragment>
              )}

              {/* ── DEINE SKILLS ─────────────────────────────────── */}
              {section2 && (
                <React.Fragment>
                  <Caly_Balken className="caly-orange">Deine Skills</Caly_Balken>
                  <Caly_BalkenInhalt>
                    {documentToReactComponents(section2.json, caly_richTextOptionen)}
                  </Caly_BalkenInhalt>
                </React.Fragment>
              )}

              {/* ── DIE DETAILS ──────────────────────────────────── */}
              {caly_details.length > 0 && (
                <React.Fragment>
                  <Caly_DetailsTitel>Die Details</Caly_DetailsTitel>
                  <Caly_DetailsGrid>
                    {caly_details.map(function caly_renderDetail(caly_punkt) {
                      return (
                        <Caly_DetailPunkt key={caly_punkt}>
                          <Caly_SternIcon />
                          {caly_punkt}
                        </Caly_DetailPunkt>
                      )
                    })}
                  </Caly_DetailsGrid>
                </React.Fragment>
              )}

              {/* ── SEI WILLKOMMEN BEI EXTENDIT ──────────────────── */}
              <Caly_WillkommenTitel>
                Sei willkommen
                <br />
                bei EXTENDIT
              </Caly_WillkommenTitel>
              <Caly_KulturGrid>
                {caly_kulturDaten.map(function caly_renderKultur(caly_item) {
                  return (
                    <Caly_KulturKarte key={caly_item.id}>
                      <Caly_KulturIcon>
                        <img src={caly_item.bild} alt="" loading="lazy" />
                      </Caly_KulturIcon>
                      <Caly_KulturText>{caly_item.text}</Caly_KulturText>
                    </Caly_KulturKarte>
                  )
                })}
              </Caly_KulturGrid>

              {/* ── TRITT MIT UNS IN KONTAKT ─────────────────────── */}
              <Caly_KontaktSektion>
                <Caly_KontaktTitel>
                  Tritt mit uns
                  <br />
                  in Kontakt
                </Caly_KontaktTitel>
                <Caly_KontaktHinweis>
                  Bitte lade hier deine Bewerbungsunterlagen hoch und wähle
                  deine Kontaktoptionen.
                </Caly_KontaktHinweis>

                <Caly_FormRow>
                  {/* Netlify-Formular mit Datei-Upload:
                      - name + hidden form-name sind Pflicht (Netlify
                        scannt das statische HTML beim Build)
                      - enctype multipart/form-data für die PDF-Uploads */}
                  <Caly_FormSpalte>
                    <form
                      name="caly-bewerbung"
                      method="POST"
                      data-netlify="true"
                      data-netlify-honeypot="bot-field"
                      encType="multipart/form-data"
                    >
                      <input
                        type="hidden"
                        name="form-name"
                        value="caly-bewerbung"
                      />
                      <input type="hidden" name="bot-field" />
                      {/* Für welche Stelle die Bewerbung eingeht */}
                      <input type="hidden" name="position" value={slug} />

                      <Caly_Input
                        type="text"
                        name="name"
                        placeholder="Name"
                        required
                      />
                      <Caly_Input
                        type="email"
                        name="email"
                        placeholder="E-Mail"
                        required
                      />
                      <Caly_Input
                        type="tel"
                        name="telefonnummer"
                        placeholder="Telefonnummer"
                      />

                      <Caly_UploadBox>
                        <label>
                          Anschreiben (PDF)
                          <input
                            type="file"
                            name="anschreiben"
                            accept="application/pdf"
                          />
                        </label>
                        <label>
                          Lebenslauf / CV (PDF)
                          <input
                            type="file"
                            name="lebenslauf"
                            accept="application/pdf"
                          />
                        </label>
                      </Caly_UploadBox>

                      <Caly_KontaktOptionen>
                        <label>
                          <input
                            type="radio"
                            name="kontaktoption"
                            value="Tel"
                            defaultChecked
                          />
                          Tel
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="kontaktoption"
                            value="E-Mail"
                          />
                          E-Mail
                        </label>
                      </Caly_KontaktOptionen>
                      <Caly_OptionenHinweis>
                        Wie dürfen wir
                        <br />
                        dich kontaktieren?
                      </Caly_OptionenHinweis>

                      <Caly_FormBtn type="submit">absenden</Caly_FormBtn>
                    </form>
                  </Caly_FormSpalte>

                  {/* Rotierender Badge "Danke für deine Bewerbung" */}
                  <Caly_BadgeWrapper aria-hidden="true">
                    <svg className="caly-badge-text" viewBox="0 0 200 200">
                      <defs>
                        <path
                          id="caly-badge-kreis"
                          d="M 100,100 m -78,0 a 78,78 0 1,1 156,0 a 78,78 0 1,1 -156,0"
                        />
                      </defs>
                      <text
                        style={{
                          fontSize: '30px',
                          fill: '#000',
                        }}
                      >
                        {/* textLength = Kreisumfang (2·π·78 ≈ 490), damit
                            der Text exakt einmal umläuft */}
                        <textPath
                          href="#caly-badge-kreis"
                          textLength="488"
                          lengthAdjust="spacing"
                        >
                          Danke für deine Bewerbung&nbsp;&nbsp;•&nbsp;&nbsp;
                        </textPath>
                      </text>
                    </svg>
                    <Caly_SternIcon className="caly-badge-stern" />
                  </Caly_BadgeWrapper>
                </Caly_FormRow>
              </Caly_KontaktSektion>
            </Caly_InhaltRechts>
          </Caly_InhaltRow>
        </Caly_Seite>
      </Container>
    </Layout>
  )
}

// ─────────────────────────────────────────────────────────────
// 5. GraphQL-Abfrage & Export
// ─────────────────────────────────────────────────────────────

export const query = graphql`
  query($slug: String!) {
    contentfulJobs(slug: { eq: $slug }) {
      title
      slug
      metaDescription {
        internal {
          content
        }
      }
      publishDate(formatString: "DD/MM/YY")
      einleitung {
        internal {
          content
        }
      }
      section1 {
        json
      }
      section2 {
        json
      }
      sexm
      sexw
      sexd
      arbeitszeit
      gehalt
      gehlterProJahr
      eintritt
      teilzeitMglich
    }
  }
`

export default Caly_Job2Template
